import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Inbox, Mail, Star, Images, LogOut, Menu } from "lucide-react";
import api from "@/lib/api";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/estimates", label: "Estimates", icon: Inbox },
  { to: "/admin/messages", label: "Messages", icon: Mail },
  { to: "/admin/testimonials", label: "Testimonials", icon: Star },
  { to: "/admin/portfolio", label: "Portfolio", icon: Images },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("vialfa-admin-token");
    if (!token) { navigate("/admin/login", { replace: true }); return; }
    api.get("/auth/me")
      .then(() => setChecking(false))
      .catch(() => {
        localStorage.removeItem("vialfa-admin-token");
        navigate("/admin/login", { replace: true });
      });
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("vialfa-admin-token");
    navigate("/admin/login", { replace: true });
  };

  if (checking) {
    return <div className="min-h-screen bg-navy flex items-center justify-center text-offwhite/50 text-sm">Loading…</div>;
  }

  const sidebar = (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3 px-6 h-20 border-b border-navy-600">
        <img src="/assets/logo-light.png" alt="VIALFA" className="h-12 w-auto" />
        <span className="ml-auto text-[10px] font-bold uppercase tracking-widest text-offwhite/40">Admin</span>
      </div>
      <nav className="flex-1 py-6 px-3 space-y-1">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={() => setOpen(false)}
            data-testid={`admin-nav-${n.label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 text-sm font-semibold transition-colors duration-200 ${
                isActive ? "bg-orange/10 text-orange border-l-2 border-orange" : "text-offwhite/60 hover:text-offwhite hover:bg-navy-700 border-l-2 border-transparent"
              }`
            }
          >
            <n.icon size={17} /> {n.label}
          </NavLink>
        ))}
      </nav>
      <button
        onClick={logout}
        data-testid="admin-logout"
        className="m-3 flex items-center gap-3 px-4 py-3 text-sm font-semibold text-offwhite/50 hover:text-red-400 transition-colors duration-200"
      >
        <LogOut size={17} /> Sign Out
      </button>
    </div>
  );

  return (
    <div data-testid="admin-layout" className="min-h-screen bg-offwhite-dark flex">
      <aside className="hidden lg:block w-64 shrink-0 bg-navy min-h-screen sticky top-0 h-screen">{sidebar}</aside>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 bg-navy/80 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <aside className="w-64 bg-navy h-full" onClick={(e) => e.stopPropagation()}>{sidebar}</aside>
        </div>
      )}
      <div className="flex-1 min-w-0">
        <header className="lg:hidden sticky top-0 z-40 bg-navy h-16 flex items-center px-4">
          <button onClick={() => setOpen(true)} data-testid="admin-mobile-menu" className="text-offwhite p-2"><Menu size={22} /></button>
          <img src="/assets/logo-light.png" alt="VIALFA" className="ml-3 h-9 w-auto" />
          <span className="ml-1 text-[10px] font-bold uppercase tracking-widest text-offwhite/40">Admin</span>
        </header>
        <main className="p-4 md:p-8 admin-scrollbar">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
